import React, { useEffect, useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  MapPin,
  Clock,
  User,
  Building2,
  Camera,
  Search,
  Filter,
  RefreshCw,
  ExternalLink,
  CheckCircle2,
  XCircle,
  FileCheck,
  Globe,
  Layers,
  Calendar,
  X,
  Eye,
  ChevronRight,
  ShieldCheck,
  Navigation
} from 'lucide-react';
import backendApi from '../../services/backendApi';

interface AttendanceLog {
  id: number;
  user_name: string;
  username: string;
  user_role: string;
  company_name: string;
  site_name: string;
  site_code: string;
  check_in_time: string;
  check_out_time: string | null;
  duration_minutes: number;
  check_in_latitude: number | null;
  check_in_longitude: number | null;
  check_in_address: string;
  check_out_latitude: number | null;
  check_out_longitude: number | null;
  status: 'ACTIVE' | 'COMPLETED';
  notes: string;
}

interface InspectionProof {
  id: number;
  user_name: string;
  username: string;
  company_name: string;
  site_name: string;
  site_code: string;
  photo: string | null;
  photo_url: string | null;
  photo_display_url?: string | null;
  latitude: number | null;
  longitude: number | null;
  address: string;
  checklist_title: string;
  notes: string;
  timestamp: string;
}

const getImageUrl = (proof: InspectionProof) => {
  const raw = proof.photo_display_url || proof.photo_url || proof.photo || '';
  if (!raw) return 'https://images.unsplash.com/photo-1509391365360-2e959784a276?w=600&auto=format&fit=crop&q=80';
  if (raw.startsWith('http://') || raw.startsWith('https://') || raw.startsWith('data:')) {
    return raw;
  }
  const protocol = window.location.protocol;
  const host = window.location.hostname;
  const port = window.location.port ? `:${window.location.port}` : '';
  const baseUrl = process.env.REACT_APP_API_URL || `${protocol}//${host}${port ? ':8000' : ''}`;
  return `${baseUrl}${raw.startsWith('/') ? '' : '/'}${raw}`;
};

const SiteAttendancePage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'logs' | 'proofs'>('logs');
  const [loading, setLoading] = useState<boolean>(true);
  const [logs, setLogs] = useState<AttendanceLog[]>([]);
  const [proofs, setProofs] = useState<InspectionProof[]>([]);

  // Search & Filters
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('ALL');

  // Preview Modal
  const [selectedProofModal, setSelectedProofModal] = useState<InspectionProof | null>(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [logsRes, proofsRes] = await Promise.all([
        backendApi.getSiteAttendanceLogs(),
        backendApi.getFieldInspectionProofs()
      ]);

      if (logsRes.success) {
        setLogs(logsRes.logs || []);
      }
      if (proofsRes.success) {
        setProofs(proofsRes.proofs || []);
      }
    } catch (e) {
      console.error('Failed to fetch site attendance data', e);
    } finally {
      setLoading(false);
    }
  };

  const filteredLogs = useMemo(() => {
    return logs.filter(item => {
      const matchesSearch = 
        (item.user_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (item.username || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (item.site_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (item.company_name || '').toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesStatus = statusFilter === 'ALL' || item.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [logs, searchQuery, statusFilter]);

  const filteredProofs = useMemo(() => {
    return proofs.filter(item => {
      return (
        (item.user_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (item.username || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (item.site_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (item.company_name || '').toLowerCase().includes(searchQuery.toLowerCase())
      );
    });
  }, [proofs, searchQuery]);

  const totalActiveCount = useMemo(() => logs.filter(l => l.status === 'ACTIVE').length, [logs]);
  const totalDurationHours = useMemo(() => {
    const totalMins = logs.reduce((acc, l) => acc + (l.duration_minutes || 0), 0);
    return (totalMins / 60).toFixed(1);
  }, [logs]);

  return (
    <div className="min-h-screen pt-20 lg:pt-4 pb-8 px-2 sm:px-3 lg:px-4">
      <div className="max-w-7xl mx-auto space-y-6">

        {/* ── HERO BANNER ────────────────────────────────────────── */}
        <motion.section
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="admin-hero p-4 sm:p-4 lg:p-5"
        >
          <div className="admin-toolbar">
            <div className="space-y-2">
              <p className="admin-eyebrow">Field Operations & GPS Tracking</p>
              <div className="flex items-start gap-4">
                <div className="hidden sm:flex w-11 h-11 rounded-[16px] bg-[linear-gradient(135deg,#0084CF,#1C4D88)] items-center justify-center text-white shadow-[0_12px_20px_rgba(0,132,207,0.2)] flex-shrink-0">
                  <MapPin size={22} />
                </div>
                <div>
                  <h1 className="admin-page-title">Site Attendance & Inspection Proofs</h1>
                  <p className="admin-page-subtitle mt-2">
                    Real-time GPS attendance tracking, on-site time duration, and geotagged field photo evidence for General Users, HO Admins & Site Admins.
                  </p>
                  <div className="flex flex-wrap gap-2 mt-4">
                    <span className="admin-pill admin-pill-info">
                      <Globe size={12} />
                      GPS Geotagged
                    </span>
                    <span className="admin-pill admin-pill-success">
                      <Clock size={12} />
                      {totalActiveCount} Currently On Site
                    </span>
                    <span className="admin-pill admin-pill-muted">
                      <Camera size={12} />
                      {proofs.length} Inspection Proofs
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                onClick={fetchData}
                className="admin-button admin-button-primary"
                title="Refresh live data" aria-label="Refresh live data"
              >
                <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
                Refresh Data
              </button>
            </div>
          </div>

          {/* Metric Stat Strip */}
          <div className="admin-stat-strip mt-6">
            <div className="admin-stat-card">
              <p className="admin-stat-label">Total Visits</p>
              <p className="admin-stat-value">{logs.length}</p>
              <p className="admin-stat-hint">Recorded GPS check-in logs</p>
            </div>
            <div className="admin-stat-card">
              <p className="admin-stat-label">Currently On Site</p>
              <p className="admin-stat-value text-[#0066A3]">{totalActiveCount}</p>
              <p className="admin-stat-hint">Active check-in sessions</p>
            </div>
            <div className="admin-stat-card">
              <p className="admin-stat-label">Total On-Site Time</p>
              <p className="admin-stat-value text-[#0084cf]">{totalDurationHours} hrs</p>
              <p className="admin-stat-hint">Accumulated plant work duration</p>
            </div>
            <div className="admin-stat-card">
              <p className="admin-stat-label">Photo Proofs</p>
              <p className="admin-stat-value">{proofs.length}</p>
              <p className="admin-stat-hint">Geotagged photographic records</p>
            </div>
          </div>
        </motion.section>

        {/* ── TABS & FILTER BAR ─────────────────────────────────── */}
        <motion.section
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="admin-section"
        >
          <div className="admin-section-header">
            <div>
              <h2 className="admin-section-title">Navigation & Filters</h2>
              <p className="admin-section-copy">Switch between attendance logs and geotagged photo proofs or search across users, sites, and companies.</p>
            </div>
          </div>
          <div className="admin-section-body">
            <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-4">
              {/* Tab Selector */}
              <div className="inline-flex p-1 rounded-2xl bg-[rgba(28, 77, 136,0.06)] border border-[rgba(28, 77, 136,0.1)] gap-1">
                <button
                  onClick={() => setActiveTab('logs')}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold transition-all ${
                    activeTab === 'logs'
                      ? 'bg-white text-[#1C4D88] shadow-sm border border-[rgba(28, 77, 136,0.12)]'
                      : 'text-[#6A7A92] hover:text-[#0B1A2F]'
                  }`}
                >
                  <Clock size={15} />
                  Site Attendance Logs ({logs.length})
                </button>
                <button
                  onClick={() => setActiveTab('proofs')}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold transition-all ${
                    activeTab === 'proofs'
                      ? 'bg-white text-[#1C4D88] shadow-sm border border-[rgba(28, 77, 136,0.12)]'
                      : 'text-[#6A7A92] hover:text-[#0B1A2F]'
                  }`}
                >
                  <Camera size={15} />
                  Geotagged Proofs ({proofs.length})
                </button>
              </div>

              {/* Search & Status Filters */}
              <div className="flex flex-wrap items-center gap-3">
                <div className="admin-search-wrap min-w-[240px] flex-1 sm:flex-initial">
                  <Search size={16} />
                  <input
                    type="text"
                    placeholder="Search user, site, or company..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    className="admin-input text-xs"
                  />
                </div>

                {activeTab === 'logs' && (
                  <select
                    value={statusFilter}
                    onChange={e => setStatusFilter(e.target.value)}
                    className="admin-select min-w-[140px] text-xs"
                  >
                    <option value="ALL">All Status</option>
                    <option value="ACTIVE">On Site (Active)</option>
                    <option value="COMPLETED">Completed</option>
                  </select>
                )}
              </div>
            </div>
          </div>
        </motion.section>

        {/* ── TAB 1: ATTENDANCE LOGS TABLE ─────────────────────── */}
        {activeTab === 'logs' && (
          <motion.section
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="admin-section"
          >
            <div className="admin-section-header">
              <div>
                <h2 className="admin-section-title">Attendance Records</h2>
                <p className="admin-section-copy">Verified check-in & check-out logs with duration calculations and GPS map markers.</p>
              </div>
              <span className="admin-pill admin-pill-info">{filteredLogs.length} Records</span>
            </div>

            <div className="admin-section-body">
              {loading ? (
                <div className="p-12 text-center text-[#6A7A92] text-sm">
                  <RefreshCw size={24} className="animate-spin mx-auto mb-3 text-[#0084CF]" />
                  Loading Attendance Logs...
                </div>
              ) : filteredLogs.length === 0 ? (
                <div className="admin-empty">
                  No site attendance logs matched the current search or status filter.
                </div>
              ) : (
                <div className="overflow-x-auto rounded-2xl border border-[rgba(28, 77, 136,0.1)] bg-white shadow-sm">
                  <table className="w-full text-left text-xs">
                    <thead>
                      <tr className="bg-[rgba(28, 77, 136,0.03)] text-[#6A7A92] uppercase font-bold text-[11px] tracking-wider border-b border-[rgba(28, 77, 136,0.08)]">
                        <th className="py-3.5 px-4">User & Role</th>
                        <th className="py-3.5 px-4">Company & Solar Site</th>
                        <th className="py-3.5 px-4">Check-In Time</th>
                        <th className="py-3.5 px-4">Check-Out Time</th>
                        <th className="py-3.5 px-4">Duration On Site</th>
                        <th className="py-3.5 px-4">GPS Location</th>
                        <th className="py-3.5 px-4 text-center">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[rgba(28, 77, 136,0.06)]">
                      {filteredLogs.map(item => (
                        <tr key={item.id} className="hover:bg-[rgba(0,132,207,0.02)] transition-colors">
                          <td className="py-3.5 px-4 font-medium text-[#0B1A2F]">
                            <div className="flex items-center gap-3">
                              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#0084CF]/15 to-[#1C4D88]/20 border border-[#0084CF]/30 flex items-center justify-center text-[#1C4D88] font-bold text-xs flex-shrink-0">
                                {(item.user_name?.[0] || item.username?.[0] || 'U').toUpperCase()}
                              </div>
                              <div className="min-w-0">
                                <div className="font-bold text-[#0B1A2F] text-xs truncate">{item.user_name || item.username}</div>
                                <div className="text-[11px] text-[#6A7A92]">@{item.username} • <span className="font-semibold text-[#0084CF]">{item.user_role}</span></div>
                              </div>
                            </div>
                          </td>

                          <td className="py-3.5 px-4 text-[#344B66]">
                            <div className="font-bold text-[#0B1A2F] text-xs">{item.site_name || item.site_code}</div>
                            <div className="text-[11px] text-[#6A7A92]">{item.company_name || 'Organization'} ({item.site_code})</div>
                          </td>

                          <td className="py-3.5 px-4 text-[#344B66] font-medium">
                            {item.check_in_time ? new Date(item.check_in_time).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' }) : '—'}
                          </td>

                          <td className="py-3.5 px-4 text-[#344B66]">
                            {item.check_out_time ? (
                              <span className="font-medium">{new Date(item.check_out_time).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}</span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-[#0066A3] font-bold bg-[#0066A3]/10 px-2 py-0.5 rounded-full text-[10px]">
                                <span className="w-1.5 h-1.5 rounded-full bg-[#0066A3] animate-ping inline-block"></span>
                                Still on Site
                              </span>
                            )}
                          </td>

                          <td className="py-3.5 px-4">
                            {item.duration_minutes ? (
                              <span className="font-bold text-[#0066A3] bg-[#0066A3]/10 px-2.5 py-1 rounded-full text-[11px] border border-[#0066A3]/20">
                                {Math.floor(item.duration_minutes / 60)}h {item.duration_minutes % 60}m
                              </span>
                            ) : (
                              <span className="font-semibold text-[#0084CF] bg-[#0084CF]/10 px-2.5 py-1 rounded-full text-[11px]">
                                In Progress
                              </span>
                            )}
                          </td>

                          <td className="py-3.5 px-4 text-[#344B66]">
                            {item.check_in_latitude && item.check_in_longitude ? (
                              <a
                                href={`https://maps.google.com/?q=${item.check_in_latitude},${item.check_in_longitude}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 text-[#0084CF] font-semibold hover:text-[#1C4D88] hover:underline bg-[#0084CF]/8 hover:bg-[#0084CF]/15 px-2.5 py-1 rounded-lg transition-all"
                              >
                                <MapPin size={13} />
                                <span>{item.check_in_latitude.toFixed(4)}, {item.check_in_longitude.toFixed(4)}</span>
                                <ExternalLink size={11} />
                              </a>
                            ) : (
                              <span className="text-[#A0AEC0]">No GPS coordinates</span>
                            )}
                          </td>

                          <td className="py-3.5 px-4 text-center">
                            <span className={`admin-pill ${item.status === 'ACTIVE' ? 'admin-pill-info' : 'admin-pill-success'}`}>
                              {item.status === 'ACTIVE' ? 'Active' : 'Completed'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </motion.section>
        )}

        {/* ── TAB 2: GEOTAGGED INSPECTION PROOFS GALLERY ───────── */}
        {activeTab === 'proofs' && (
          <motion.section
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="admin-section"
          >
            <div className="admin-section-header">
              <div>
                <h2 className="admin-section-title">Geotagged Photo Proofs Gallery</h2>
                <p className="admin-section-copy">On-ground field photo evidence uploaded by site engineers and maintenance technicians.</p>
              </div>
              <span className="admin-pill admin-pill-info">{filteredProofs.length} Proofs</span>
            </div>

            <div className="admin-section-body">
              {loading ? (
                <div className="p-12 text-center text-[#6A7A92] text-sm">
                  <RefreshCw size={24} className="animate-spin mx-auto mb-3 text-[#0084CF]" />
                  Loading Field Inspection Proofs...
                </div>
              ) : filteredProofs.length === 0 ? (
                <div className="admin-empty">
                  No field inspection photo proofs uploaded yet.
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {filteredProofs.map(proof => (
                    <motion.div
                      key={proof.id}
                      whileHover={{ y: -3 }}
                      onClick={() => setSelectedProofModal(proof)}
                      className="group bg-white border border-[rgba(28, 77, 136,0.12)] hover:border-[#0084CF]/40 rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all cursor-pointer flex flex-col"
                    >
                      {/* Photo Thumbnail */}
                      <div className="relative h-44 bg-slate-100 overflow-hidden">
                        <img
                          src={getImageUrl(proof)}
                          alt={proof.checklist_title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                        <div className="absolute top-2 right-2">
                          <span className="px-2 py-1 bg-white/90 backdrop-blur-md rounded-lg text-[10px] font-bold text-[#1C4D88] shadow-sm flex items-center gap-1 border border-white/40">
                            <Eye size={11} /> View
                          </span>
                        </div>
                        <div className="absolute bottom-2 left-2 right-2 px-2.5 py-1.5 bg-[#0B1A2F]/80 backdrop-blur-md rounded-xl text-[10px] text-white flex items-center justify-between">
                          <span className="truncate font-semibold flex items-center gap-1">
                            <MapPin size={11} className="text-[#0084CF]" />
                            {proof.latitude ? `${proof.latitude.toFixed(4)}, ${proof.longitude?.toFixed(4)}` : 'No GPS'}
                          </span>
                          <span className="text-slate-300 font-medium">
                            {new Date(proof.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </div>
                      </div>

                      {/* Card Information */}
                      <div className="p-3.5 space-y-1.5 flex-1 flex flex-col justify-between">
                        <div>
                          <div className="font-bold text-xs text-[#0B1A2F] truncate">{proof.site_name || 'Solar Plant'}</div>
                          <div className="text-[11px] text-[#6A7A92] truncate flex items-center gap-1 mt-0.5">
                            <User size={12} className="text-[#0084CF]" />
                            {proof.user_name || proof.username}
                          </div>
                        </div>

                        <div className="pt-2 border-t border-[rgba(28, 77, 136,0.06)] flex items-center justify-between">
                          <span className="text-[10px] font-bold text-[#1C4D88] bg-[#0084CF]/10 px-2 py-0.5 rounded-md truncate max-w-[150px]">
                            {proof.checklist_title || 'General Inspection'}
                          </span>
                          <span className="text-[10px] text-[#6A7A92]">
                            {new Date(proof.timestamp).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          </motion.section>
        )}

        {/* ── LIGHTBOX INSPECTION PROOF MODAL ─────────────────── */}
        {selectedProofModal && (
          <div className="fixed inset-0 z-50 bg-[#0B1A2F]/60 backdrop-blur-sm flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="admin-modal-shell max-w-lg w-full overflow-hidden shadow-2xl"
            >
              <div className="admin-modal-header flex items-center justify-between">
                <div>
                  <h3 className="admin-modal-title text-base font-bold text-[#0B1A2F]">Geotagged Photo Evidence</h3>
                  <p className="text-xs text-[#6A7A92] mt-0.5">Inspection item details and GPS coordinates</p>
                </div>
                <button
                  onClick={() => setSelectedProofModal(null)}
                  className="w-8 h-8 rounded-full bg-[rgba(28, 77, 136,0.06)] hover:bg-[rgba(28, 77, 136,0.12)] flex items-center justify-center text-[#6A7A92] hover:text-[#0B1A2F] transition-all"
                >
                  <X size={16} />
                </button>
              </div>

              <div className="admin-modal-body space-y-4">
                <div className="rounded-2xl overflow-hidden border border-[rgba(28, 77, 136,0.12)] bg-slate-100 shadow-inner">
                  <img
                    src={getImageUrl(selectedProofModal)}
                    alt="Proof"
                    className="w-full h-64 object-cover"
                  />
                </div>

                <div className="p-3.5 rounded-2xl bg-[rgba(28, 77, 136,0.03)] border border-[rgba(28, 77, 136,0.08)] space-y-2 text-xs">
                  <div className="flex justify-between py-1 border-b border-[rgba(28, 77, 136,0.06)]">
                    <span className="text-[#6A7A92] font-semibold">Site Name:</span>
                    <span className="font-bold text-[#0B1A2F]">{selectedProofModal.site_name}</span>
                  </div>
                  <div className="flex justify-between py-1 border-b border-[rgba(28, 77, 136,0.06)]">
                    <span className="text-[#6A7A92] font-semibold">Field Inspector:</span>
                    <span className="font-bold text-[#0B1A2F]">{selectedProofModal.user_name} (@{selectedProofModal.username})</span>
                  </div>
                  <div className="flex justify-between py-1 border-b border-[rgba(28, 77, 136,0.06)]">
                    <span className="text-[#6A7A92] font-semibold">Checklist Item:</span>
                    <span className="font-bold text-[#0084CF]">{selectedProofModal.checklist_title || 'Field Inspection'}</span>
                  </div>
                  {selectedProofModal.notes && (
                    <div className="flex justify-between py-1 border-b border-[rgba(28, 77, 136,0.06)]">
                      <span className="text-[#6A7A92] font-semibold">Inspector Notes:</span>
                      <span className="font-medium text-[#0B1A2F]">{selectedProofModal.notes}</span>
                    </div>
                  )}
                  <div className="flex justify-between py-1 border-b border-[rgba(28, 77, 136,0.06)]">
                    <span className="text-[#6A7A92] font-semibold">GPS Coordinates:</span>
                    {selectedProofModal.latitude && selectedProofModal.longitude ? (
                      <a
                        href={`https://maps.google.com/?q=${selectedProofModal.latitude},${selectedProofModal.longitude}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-bold text-[#0084CF] hover:underline flex items-center gap-1"
                      >
                        <MapPin size={12} />
                        {selectedProofModal.latitude.toFixed(4)}, {selectedProofModal.longitude.toFixed(4)}
                        <ExternalLink size={10} />
                      </a>
                    ) : (
                      <span className="text-[#A0AEC0]">No GPS data recorded</span>
                    )}
                  </div>
                  <div className="flex justify-between py-1">
                    <span className="text-[#6A7A92] font-semibold">Captured Timestamp:</span>
                    <span className="font-semibold text-[#344B66]">{new Date(selectedProofModal.timestamp).toLocaleString()}</span>
                  </div>
                </div>
              </div>

              <div className="admin-modal-footer">
                <button
                  onClick={() => setSelectedProofModal(null)}
                  className="admin-button admin-button-secondary"
                >
                  Close
                </button>
              </div>
            </motion.div>
          </div>
        )}

      </div>
    </div>
  );
};

export default SiteAttendancePage;

